import React, { useRef, useEffect } from 'react';
import FilterListIcon from '@mui/icons-material/FilterList';
import styles from './JournalEntryTypeFilter.module.scss';

const JournalEntryTypeFilter = ({
    t,
    direction,
    selectedJournalEntryTypes,
    setSelectedJournalEntryTypes,
    journalEntryTypeDropdownOpen,
    setJournalEntryTypeDropdownOpen,
    journalEntryTypeOptions
}) => {
    const journalEntryTypeDropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (journalEntryTypeDropdownRef.current && 
                !journalEntryTypeDropdownRef.current.contains(event.target)) {
                setJournalEntryTypeDropdownOpen(false);
            }
        };

        if (journalEntryTypeDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [journalEntryTypeDropdownOpen, setJournalEntryTypeDropdownOpen]);

    return (
        <div
            ref={journalEntryTypeDropdownRef}
            className={`${styles.wrap} ${journalEntryTypeDropdownOpen ? styles.selectOpen : ''} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
        >
            <button
                type="button"
                className={`${styles.trigger} ${journalEntryTypeDropdownOpen ? styles.triggerOpen : ''}`}
                onClick={() => setJournalEntryTypeDropdownOpen(!journalEntryTypeDropdownOpen)}
                aria-expanded={journalEntryTypeDropdownOpen}
                aria-haspopup="menu"
                title={t('JournalEntryType')}
            >
                <span className={styles.icon} aria-hidden="true">
                    <FilterListIcon sx={{ fontSize: 18 }} />
                </span>
                {selectedJournalEntryTypes.length > 0 ? (
                    <span className={styles.chips}>
                        {selectedJournalEntryTypes
                            .reduce((unique, s) => {
                                const currentValue = typeof s === 'object' && s.value ? s.value : s;
                                const exists = unique.some(item => {
                                    const itemValue = typeof item === 'object' && item.value ? item.value : item;
                                    return itemValue === currentValue;
                                });
                                if (!exists) {
                                    unique.push(s);
                                }
                                return unique;
                            }, [])
                            .map((s) => {
                                const displayLabel = (() => {
                                    if (typeof s === 'object' && s.label) {
                                        return t(s.label);
                                    } else if (typeof s === 'object' && s.value !== undefined) {
                                        const typeOption = journalEntryTypeOptions.find(option => option.value === s.value);
                                        return typeOption ? t(typeOption.label) : s.value;
                                    } else {
                                        const typeOption = journalEntryTypeOptions.find(option => option.value === s);
                                        return typeOption ? t(typeOption.label) : s;
                                    }
                                })();

                                const uniqueKey = typeof s === 'object' && s.value !== undefined ? s.value : s;

                                return (
                                    <span
                                        key={uniqueKey}
                                        className={styles.chip}
                                    >
                                        <span className={styles.chipLabel}>{displayLabel}</span>
                                        <span
                                            className={styles.chipRemove}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedJournalEntryTypes(selectedJournalEntryTypes.filter(item => {
                                                    const itemValue = typeof item === 'object' && item.value !== undefined ? item.value : item;
                                                    return itemValue !== uniqueKey;
                                                }));
                                            }}
                                        >
                                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                                <path 
                                                    d="M7.5 2.5L2.5 7.5M2.5 2.5L7.5 7.5" 
                                                    stroke="#304FFF" 
                                                    strokeWidth="1.5" 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </span>
                                );
                            })}
                    </span>
                ) : (
                    <span className={styles.triggerText}>{t('JournalEntryType')}</span>
                )}
                <span className={styles.chevron} aria-hidden="true" />
            </button>
                    
            {journalEntryTypeDropdownOpen && (
                <div className={styles.list} role="menu" aria-label={t('JournalEntryType')}>
                    {/* Select All Option */}
                    <button
                        type="button"
                        className={styles.option}
                        onClick={() => {
                            if (selectedJournalEntryTypes.length === journalEntryTypeOptions.length) {
                                setSelectedJournalEntryTypes([]);
                            } else {
                                setSelectedJournalEntryTypes([...journalEntryTypeOptions]);
                            }
                        }}
                    >
                        <span className={styles.optionLabel}>
                            {selectedJournalEntryTypes.length === journalEntryTypeOptions.length ? t('DeselectAll') : t('SelectAll')}
                        </span>
                    </button>
                            
                    {journalEntryTypeOptions.map((option) => (
                        <button
                            type="button"
                            key={option.value}
                            className={`${styles.option} ${selectedJournalEntryTypes.some(s => {
                                if (typeof s === 'object' && s.value !== undefined) {
                                    return s.value === option.value;
                                } else {
                                    return s === option.value;
                                }
                            }) ? styles.optionSelected : ''}`}
                            onClick={() => {
                                const isSelected = selectedJournalEntryTypes.some(s => {
                                    if (typeof s === 'object' && s.value !== undefined) {
                                        return s.value === option.value;
                                    } else {
                                        return s === option.value;
                                    }
                                });
                                        
                                if (isSelected) {
                                    const newSelectedTypes = selectedJournalEntryTypes.filter(s => {
                                        if (typeof s === 'object' && s.value !== undefined) {
                                            return s.value !== option.value;
                                        } else {
                                            return s !== option.value;
                                        }
                                    });
                                    setSelectedJournalEntryTypes(newSelectedTypes);
                                } else {
                                    const newSelectedTypes = [...selectedJournalEntryTypes, option];
                                    setSelectedJournalEntryTypes(newSelectedTypes);
                                }
                            }}
                        >
                            <span className={styles.optionLabel}>{t(option.label)}</span>
                            {selectedJournalEntryTypes.some(s => {
                                if (typeof s === 'object' && s.value !== undefined) {
                                    return s.value === option.value;
                                } else {
                                    return s === option.value;
                                }
                            }) && (
                                <svg className={styles.check} width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path 
                                        d="M13.5 4.5L6 12L2.5 8.5" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default JournalEntryTypeFilter;
